/**
 * Quick View Component
 * Handles quick view modal functionality for product cards
 */

(function() {
  'use strict';

  let currentModal = null;

  /**
   * Resolve the desktop overlay / mobile modal element for a given product, scoped
   * to a specific section when possible. Falls back to the legacy non-namespaced id
   * for sections that haven't adopted `data-section-id` yet so quick view keeps
   * working everywhere.
   *
   * Without this scoping, two sections rendering the same product (e.g. both
   * "Homepage Landing Product" sections whose collections share a product) would
   * each emit `id="quick-view-overlay-{productId}"` and `getElementById` would
   * always resolve to the first section — silently breaking quick view in any
   * subsequent section.
   */
  function findQuickViewOverlay(productId, sectionId) {
    if (sectionId) {
      const scoped = document.getElementById(`quick-view-overlay-${sectionId}-${productId}`);
      if (scoped) return scoped;
    }
    return document.getElementById(`quick-view-overlay-${productId}`);
  }

  function findQuickViewModal(productId, sectionId) {
    if (sectionId) {
      const scoped = document.getElementById(`quick-view-${sectionId}-${productId}`);
      if (scoped) return scoped;
    }
    return document.getElementById(`quick-view-${productId}`);
  }

  /**
   * Locate the source product card. Prefers the same-section card so a swatch
   * change in section A doesn't sync into section B's stale overlay.
   */
  function findProductCard(productId, sectionId) {
    if (sectionId) {
      const scoped = document.querySelector(
        `.product-card[data-section-id="${sectionId}"][data-product-id="${productId}"]`
      );
      if (scoped) return scoped;
    }
    return document.querySelector(`.product-card[data-product-id="${productId}"]`);
  }

  // Initialize quick view functionality
  function init() {
    // Listen for quick view button clicks
    document.addEventListener('click', handleQuickViewClick);
    
    // Close modal on overlay or close button click
    document.addEventListener('click', handleCloseClick);
    
    // Close on Escape key
    document.addEventListener('keydown', handleEscapeKey);
    
    // Handle variant selection
    document.addEventListener('click', handleVariantSelection);
    
    // Handle seater product switching (without page navigation)
    document.addEventListener('click', handleSeaterSelection);
    
    // Handle add to cart with loader - use capture phase to run before global handler
    document.addEventListener('click', handleAddToCart, true);
    
    // Sync color selection from product card to quick view
    syncColorFromProductCard();

    // Allow mouse-wheel users to scroll the horizontal size-buttons row.
    // The buttons live inside a Swiper-driven product slider on desktop; the
    // `swiper-no-swiping` class on the container already stops Swiper from
    // hijacking drag gestures, but a vertical mouse wheel still doesn't
    // translate to horizontal scroll by default, so we wire that up here.
    enableHorizontalWheelScroll();

    // Let desktop users click-and-drag (grab) the size-buttons row to scroll
    // it forward/backward. Touch scrolling is left to the browser.
    enableGrabScroll();
  }

  function enableHorizontalWheelScroll() {
    document.addEventListener(
      'wheel',
      function (e) {
        const sizeButtons = e.target.closest(
          '.quick-view-content__size-buttons, .quick-view-modal__size-buttons'
        );
        if (!sizeButtons) return;
        if (sizeButtons.scrollWidth <= sizeButtons.clientWidth) return;
        // Only intercept when the user's intent is vertical wheel (mouse).
        // Trackpad horizontal swipes already produce deltaX and scroll natively.
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        e.preventDefault();
        sizeButtons.scrollLeft += e.deltaY;
      },
      { passive: false }
    );
  }

  function enableGrabScroll() {
    const SIZE_BUTTONS_SELECTOR =
      '.quick-view-content__size-buttons, .quick-view-modal__size-buttons';
    // Don't treat tiny mouse jitter on click as a drag (would suppress variant
    // selection). Threshold is the smallest move distance that "feels like" a drag.
    const DRAG_THRESHOLD_PX = 5;

    let activeContainer = null;
    let startX = 0;
    let startScrollLeft = 0;
    let activePointerId = null;
    let isDragging = false;
    let suppressNextClick = false;

    document.addEventListener('pointerdown', function (e) {
      // Mouse only — touch already scrolls natively and shouldn't fight us.
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      const container = e.target.closest(SIZE_BUTTONS_SELECTOR);
      if (!container) return;
      if (container.scrollWidth <= container.clientWidth) return;

      activeContainer = container;
      startX = e.clientX;
      startScrollLeft = container.scrollLeft;
      activePointerId = e.pointerId;
      isDragging = false;
    });

    document.addEventListener('pointermove', function (e) {
      if (!activeContainer || e.pointerId !== activePointerId) return;
      const dx = e.clientX - startX;
      if (!isDragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
        isDragging = true;
        activeContainer.classList.add('is-grabbing');
        try { activeContainer.setPointerCapture(activePointerId); } catch (_) {}
      }
      activeContainer.scrollLeft = startScrollLeft - dx;
      e.preventDefault();
    });

    function endDrag(e) {
      if (!activeContainer) return;
      if (e && e.pointerId !== activePointerId) return;
      if (isDragging) {
        // Swallow the synthetic click that fires after a mouse drag so we don't
        // accidentally pick the size variant the cursor happens to be over.
        suppressNextClick = true;
        activeContainer.classList.remove('is-grabbing');
        try { activeContainer.releasePointerCapture(activePointerId); } catch (_) {}
      }
      activeContainer = null;
      activePointerId = null;
      isDragging = false;
    }

    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);

    // Capture phase so we run before handleVariantSelection's bubble-phase click.
    document.addEventListener(
      'click',
      function (e) {
        if (!suppressNextClick) return;
        const inContainer = e.target.closest(SIZE_BUTTONS_SELECTOR);
        suppressNextClick = false;
        if (!inContainer) return;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
  }
  
  function syncColorFromProductCard() {
    // Listen for color swatch changes in product cards (within product-card__options)
    document.addEventListener('change', function(e) {
      const colorInput = e.target.closest('.product-form__color-swatch-input');
      if (!colorInput) return;
      
      handleColorChange(colorInput);
    });
    
    // Also listen for click events on color swatches to catch immediate changes
    document.addEventListener('click', function(e) {
      const colorInput = e.target.closest('.product-form__color-swatch-input');
      if (colorInput) {
        // Use setTimeout to ensure the input is checked after the click
        setTimeout(() => {
          if (colorInput.checked) {
            handleColorChange(colorInput);
          }
        }, 0);
      }
    });
    
    // Watch for changes in product-form__color-swatches-wrapper using MutationObserver
    const observeColorSwatches = () => {
      const productCards = document.querySelectorAll('.product-card');
      productCards.forEach(productCard => {
        const colorSwatchesWrapper = productCard.querySelector('.product-form__color-swatches-wrapper');
        if (colorSwatchesWrapper && !colorSwatchesWrapper.dataset.observed) {
          colorSwatchesWrapper.dataset.observed = 'true';
          
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Check if any input's checked state changed
              if (mutation.type === 'attributes' && mutation.attributeName === 'checked') {
                const colorInput = mutation.target;
                if (colorInput.classList.contains('product-form__color-swatch-input') && colorInput.checked) {
                  handleColorChange(colorInput);
                }
              }
              // Check if any child was added/removed (swatch might have been toggled)
              if (mutation.type === 'childList') {
                const checkedInput = colorSwatchesWrapper.querySelector('.product-form__color-swatch-input:checked');
                if (checkedInput) {
                  handleColorChange(checkedInput);
                }
              }
            });
          });
          
          // Observe changes to checked attributes and child list
          observer.observe(colorSwatchesWrapper, {
            attributes: true,
            attributeFilter: ['checked', 'class'],
            childList: true,
            subtree: true
          });
        }
      });
    };
    
    // Initial observation
    observeColorSwatches();
    
    // Re-observe when new product cards are added (for dynamic content)
    const productCardsObserver = new MutationObserver(() => {
      observeColorSwatches();
    });
    
    productCardsObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Also listen for variant change events from variant-radios
    document.addEventListener('variant:change', function(e) {
      const variantRadios = e.target.closest('variant-radios');
      if (!variantRadios) return;
      
      const productCard = variantRadios.closest('.product-card');
      if (!productCard) return;
      
      const productId = productCard.getAttribute('data-product-id');
      if (!productId) return;
      
      // Find checked color input
      const checkedColorInput = productCard.querySelector('.product-form__color-swatch-input:checked');
      if (checkedColorInput) {
        handleColorChange(checkedColorInput);
      }
    });
  }
  
  function syncCurrentColorFromProductCard(productId, quickView) {
    // Prefer scoping by .product-card to avoid matching unrelated elements
    // (discount-badge, dynamic-price, quick-view-overlay, etc. all carry data-product-id).
    // When the same product is rendered across multiple sections we further scope
    // the card lookup to the quick view's section so we sync from the card the
    // user actually opened the modal from (not the first match in the DOM).
    const sectionId = quickView && quickView.getAttribute && quickView.getAttribute('data-section-id');
    const productCard = findProductCard(productId, sectionId);
    if (!productCard) return;
    
    const checkedColorInput = productCard.querySelector('.product-form__color-swatch-input:checked');
    if (checkedColorInput) {
      handleColorChange(checkedColorInput);
    }
  }
  
  function handleColorChange(colorInput) {
    const productCard = colorInput.closest('.product-card');
    if (!productCard) return;
    
    const productId = productCard.getAttribute('data-product-id');
    if (!productId) return;
    
    const colorIndexAttr = colorInput.getAttribute('data-index');
    if (!colorIndexAttr) return;
    
    const colorOptionIndex = parseInt(colorIndexAttr.replace('option', '')) - 1;
    if (isNaN(colorOptionIndex)) return;
    
    const selectedColor = colorInput.value;
    
    // Update BOTH the desktop overlay and the mobile modal so they stay in sync
    // regardless of which is currently open. The previous "isOpen" check used a
    // faulty `style.display !== 'none'` test that always returned true for the
    // overlay (it has no inline style), causing mobile add-to-cart to use stale
    // variant data because only the hidden overlay was being updated.
    //
    // We also scope these lookups to the swatch's own section so changing the
    // color in section B doesn't leak into section A's quick view (which would
    // happen with plain `getElementById` because both sections render the same
    // product id and only the first match would be returned).
    const sectionId = productCard.getAttribute('data-section-id');
    // Desktop overlay lives inside the product card, so prefer the in-card
    // lookup — it's intrinsically section-scoped without needing the id.
    const quickViewOverlay = productCard.querySelector('.product-card__quick-view-overlay')
      || findQuickViewOverlay(productId, sectionId);
    const quickViewModal = findQuickViewModal(productId, sectionId);
    
    [quickViewOverlay, quickViewModal].forEach(quickView => {
      if (!quickView) return;
      
      // After a seater switch the hidden color input's data-product-id has been re-pointed
      // to the new product, so we accept either the original-product match or any color
      // input inside this quick view (there's only one).
      const quickViewColorInput = quickView.querySelector(`.quick-view-content__current-color[data-product-id="${productId}"]`)
        || quickView.querySelector('.quick-view-content__current-color');
      if (quickViewColorInput) {
        quickViewColorInput.value = selectedColor;
      }
      
      updateVariantForColorChange(quickView, productId, selectedColor, colorOptionIndex);
    });
  }
  
  /**
   * Resolve the variants list + option indexes the quick view should be operating on.
   * Prefers seater data stored on the modal (when the user has switched seaters in the
   * quick view) so subsequent size/color interactions don't fall back to the original
   * product card's variant list.
   */
  function getQuickViewVariantsContext(quickView, fallbackProductId, fallbackColorOptionIndex) {
    const modal = quickView && quickView.classList && (
      quickView.classList.contains('quick-view-modal') ||
      quickView.classList.contains('product-card__quick-view-overlay')
    )
      ? quickView
      : (quickView && quickView.closest ? quickView.closest('.quick-view-modal, .product-card__quick-view-overlay') : null);

    if (modal && modal._seaterContext) {
      return modal._seaterContext;
    }

    const productId = (modal && modal.getAttribute('data-product-id')) || fallbackProductId;
    if (!productId) return null;

    // Prefer the source card in the modal's own section so the variants script we
    // parse below belongs to the card the user actually opened the quick view from.
    const sectionId = modal && modal.getAttribute && modal.getAttribute('data-section-id');
    const productCard = findProductCard(productId, sectionId);
    if (!productCard) return null;

    const variantRadios = productCard.querySelector('variant-radios');
    if (!variantRadios) return null;

    const variantsScript = variantRadios.querySelector('script[type="application/json"]');
    if (!variantsScript) return null;

    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent || '[]');
    } catch (e) {
      return null;
    }

    return {
      productId,
      variants,
      colorOptionIndex: typeof fallbackColorOptionIndex === 'number' ? fallbackColorOptionIndex : -1,
      sizeOptionIndex: -1,
      variantDisplayPrices: null
    };
  }

  /**
   * Recompute every size button's variant data + availability for the given color.
   * Without this, switching seaters or colors only updated the active size button —
   * other size buttons retained their default (first-variant-for-that-size) data, so
   * clicking them later would either pick a wrong-color variant or be wrongly marked
   * available when the color+size combination was actually sold out.
   */
  function applyColorToSizeButtons(quickViewContent, ctx, selectedColor) {
    if (!ctx || !Array.isArray(ctx.variants)) return;
    const colorIdx = ctx.colorOptionIndex;
    const buttons = quickViewContent.querySelectorAll('.quick-view-content__size-button, .quick-view-modal__size-button');
    buttons.forEach(btn => {
      const sizeValue = btn.getAttribute('data-size-value');
      const sizeIdx = parseInt(btn.getAttribute('data-size-option-index'));
      if (isNaN(sizeIdx)) return;

      let candidate = null;
      if (selectedColor && colorIdx >= 0 && sizeValue) {
        candidate = ctx.variants.find(v =>
          v.options[colorIdx] === selectedColor && v.options[sizeIdx] === sizeValue
        ) || null;
      }
      if (!candidate && sizeValue) {
        candidate = ctx.variants.find(v => v.options[sizeIdx] === sizeValue) || null;
      }

      if (candidate) {
        btn.setAttribute('data-variant-id', candidate.id);
        const price = (ctx.variantDisplayPrices && ctx.variantDisplayPrices[String(candidate.id)]) || formatMoney(candidate.price);
        btn.setAttribute('data-price', price);
        // If a color is selected, availability must be evaluated against (color, size).
        // Only the candidate matching both is "available" for purchase.
        const availableForSelection = selectedColor && colorIdx >= 0
          ? Boolean(candidate.available && candidate.options[colorIdx] === selectedColor)
          : Boolean(candidate.available);
        btn.setAttribute('data-available', availableForSelection ? 'true' : 'false');
      } else if (selectedColor && colorIdx >= 0) {
        btn.removeAttribute('data-variant-id');
        btn.setAttribute('data-available', 'false');
      }
    });
  }

  function updateVariantForColorChange(quickView, productId, selectedColor, colorOptionIndex) {
    if (colorOptionIndex < 0) return; // No color option

    // Find the active size button
    const activeSizeButton = quickView.querySelector('.quick-view-content__size-button--active, .quick-view-modal__size-button--active');
    if (!activeSizeButton) {
      // If no active size button, try to get the first size button or update directly
      const firstSizeButton = quickView.querySelector('.quick-view-content__size-button, .quick-view-modal__size-button');
      if (firstSizeButton) {
        // Activate the first button if none is active
        firstSizeButton.classList.add('quick-view-content__size-button--active', 'quick-view-modal__size-button--active');
        updateVariantForColorChange(quickView, productId, selectedColor, colorOptionIndex);
        return;
      } else {
        // No size buttons, update add to cart directly with color info
        updateAddToCartButtonDirectly(quickView, productId, selectedColor, colorOptionIndex);
        return;
      }
    }

    const ctx = getQuickViewVariantsContext(quickView, productId, colorOptionIndex);
    if (!ctx) {
      updateAddToCartButton(activeSizeButton, quickView);
      return;
    }

    // Apply color filter to every size button so subsequent size clicks resolve correctly
    const quickViewContent = quickView.querySelector('.quick-view-content') || quickView;
    applyColorToSizeButtons(quickViewContent, ctx, selectedColor);

    // Update ATC button from the (now color-aware) active size button
    updateAddToCartButton(activeSizeButton, quickView);
  }

  function updateAddToCartButtonDirectly(quickView, productId, selectedColor, colorOptionIndex) {
    const ctx = getQuickViewVariantsContext(quickView, productId, colorOptionIndex);
    if (!ctx) return;
    const variants = ctx.variants;
    if (!Array.isArray(variants)) return;
    
    // Try to find any variant with the selected color
    const matchingVariant = variants.find(variant => {
      if (selectedColor && variant.options[colorOptionIndex] !== selectedColor) return false;
      return true;
    });
    
    const addToCartButton = quickView.querySelector('.quick-view-content__add-to-cart, .quick-view-modal__add-to-cart');
    if (!addToCartButton) return;
    
    if (matchingVariant) {
      addToCartButton.setAttribute('data-id', matchingVariant.id);
      const displayPrice = getQuickViewVariantDisplayPrice(quickView, matchingVariant.id);
      addToCartButton.textContent = `Add to Cart - ${displayPrice || formatMoney(matchingVariant.price)}`;
      addToCartButton.disabled = !matchingVariant.available;
      if (!matchingVariant.available) {
        addToCartButton.setAttribute('aria-disabled', 'true');
        addToCartButton.textContent = 'Sold Out';
      } else {
        addToCartButton.removeAttribute('aria-disabled');
      }
    } else {
      addToCartButton.removeAttribute('data-id');
      addToCartButton.disabled = true;
      addToCartButton.setAttribute('aria-disabled', 'true');
      addToCartButton.textContent = 'Sold Out';
    }
  }
  
  function handleSeaterSelection(e) {
    const button = e.target.closest('.number-of-seats__option');
    if (!button || button.disabled || button.classList.contains('number-of-seats__option--active')) return;

    const quickViewContent = button.closest('.quick-view-content');
    if (!quickViewContent) return;

    const productId = button.getAttribute('data-seater-product-id');
    if (!productId) return;

    const dataScript = quickViewContent.querySelector('script.quick-view-seater-data');
    if (!dataScript) return;

    let allData;
    try {
      allData = JSON.parse(dataScript.textContent);
    } catch (err) {
      return;
    }

    const productData = allData[productId];
    if (!productData) return;

    const priceScript = quickViewContent.querySelector('.quick-view-variant-display-prices');
    if (priceScript && productData.variantDisplayPrices) {
      priceScript.textContent = JSON.stringify(productData.variantDisplayPrices);
    }

    e.preventDefault();
    e.stopPropagation();

    quickViewContent.querySelectorAll('.number-of-seats__option').forEach(opt => {
      opt.classList.remove('number-of-seats__option--active');
    });
    button.classList.add('number-of-seats__option--active');

    const dynamicHeading = quickViewContent.querySelector('.dynamic-variant-heading');
    if (dynamicHeading) {
      dynamicHeading.textContent = ' ' + productData.seats + '-Seater ';
    }

    const titleEl = quickViewContent.querySelector('.quick-view-content__title');
    if (titleEl) titleEl.textContent = productData.title;

    const metafieldTextEl = quickViewContent.querySelector('.quick-view-content__metafield-text');
    if (metafieldTextEl) {
      if (productData.quickViewText) {
        metafieldTextEl.innerHTML = productData.quickViewText;
        metafieldTextEl.removeAttribute('hidden');
      } else {
        metafieldTextEl.innerHTML = '';
        metafieldTextEl.setAttribute('hidden', '');
      }
    }

    const featuresEl = quickViewContent.querySelector('.quick-view-content__features');
    if (featuresEl) {
      const titleH3 = featuresEl.querySelector('.quick-view-content__features-title');
      const listUl = featuresEl.querySelector('.quick-view-content__features-list');

      if (productData.additionalDetails || (productData.features && productData.features.length > 0)) {
        if (titleH3) titleH3.textContent = productData.additionalDetails || '';
        if (listUl) {
          listUl.innerHTML = '';
          if (productData.features) {
            productData.features.forEach(function(feature) {
              const li = document.createElement('li');
              li.className = 'quick-view-content__feature-item';
              const checkbox = document.createElement('span');
              checkbox.className = 'quick-view-content__feature-checkbox';
              const text = document.createElement('span');
              text.textContent = feature;
              li.appendChild(checkbox);
              li.appendChild(text);
              listUl.appendChild(li);
            });
          }
        }
        featuresEl.removeAttribute('hidden');
      } else {
        featuresEl.setAttribute('hidden', '');
      }
    }

    rebuildSizeSelector(quickViewContent, productData);

    // Stash the active seater's variant data on the modal so subsequent size/color
    // interactions resolve against THIS product (not the original product card the
    // quick view was opened from). Also re-point the modal's data-product-id so
    // selectVariant() reads the correct id.
    const modal = quickViewContent.closest('.quick-view-modal, .product-card__quick-view-overlay');
    const seaterContext = {
      productId: productData.id,
      variants: productData.variants || [],
      colorOptionIndex: typeof productData.colorOptionIndex === 'number' ? productData.colorOptionIndex : -1,
      sizeOptionIndex: typeof productData.sizeOptionIndex === 'number' ? productData.sizeOptionIndex : -1,
      variantDisplayPrices: productData.variantDisplayPrices || null
    };
    if (modal) {
      modal._seaterContext = seaterContext;
      modal.setAttribute('data-product-id', productData.id);
    }

    // Re-point the hidden color input + wrapper so future syncs land on the right node
    const colorInputEl = quickViewContent.querySelector('.quick-view-content__current-color');
    const selectedColor = colorInputEl ? colorInputEl.value : '';
    if (colorInputEl) {
      colorInputEl.setAttribute('data-product-id', productData.id);
    }
    const colorSelectorEl = quickViewContent.querySelector('.quick-view-content__color-selector');
    if (colorSelectorEl) {
      colorSelectorEl.setAttribute('data-product-id', productData.id);
      if (productData.colorOptionIndex >= 0) {
        colorSelectorEl.setAttribute('data-color-option-index', productData.colorOptionIndex);
      }
    }

    // Determine whether the new seater actually shows size buttons. rebuildSizeSelector
    // hides the size selector (without removing children) when the new product has no
    // sizeValues, so we must check the visibility / button count rather than just the
    // existence of `.quick-view-content__size-button` nodes.
    const sizeSelectorEl = quickViewContent.querySelector('.quick-view-content__size-selector');
    const sizeSelectorHidden = sizeSelectorEl && (sizeSelectorEl.hasAttribute('hidden') || sizeSelectorEl.style.display === 'none');
    const hasSizeButtons = !sizeSelectorHidden
      && Array.isArray(productData.sizeValues)
      && productData.sizeValues.length > 0;

    const addToCartBtn = quickViewContent.querySelector('.quick-view-content__add-to-cart');
    if (addToCartBtn) {
      if (productData.freeGiftId) {
        addToCartBtn.setAttribute('data-free-gift', productData.freeGiftId);
      } else {
        addToCartBtn.removeAttribute('data-free-gift');
      }
    }

    const atcModal = modal || quickViewContent.closest('.quick-view-modal, .product-card__quick-view-overlay');

    if (hasSizeButtons) {
      // Preserve the user's color selection across the seater switch by recomputing every
      // size button's variant data + availability against (selectedColor, sizeValue).
      // Without this the rebuilt size buttons would always point at the first variant for
      // each size (effectively the new product's default color).
      applyColorToSizeButtons(quickViewContent, seaterContext, selectedColor);

      // Pick the active size button:
      //  1) Prefer one whose data-available is "true" so the ATC isn't stuck in "Sold Out"
      //     when the user's color has at least one available size.
      //  2) Otherwise keep whatever rebuildSizeSelector marked active.
      const allSizeBtns = Array.from(quickViewContent.querySelectorAll('.quick-view-content__size-button, .quick-view-modal__size-button'));
      const previouslyActive = quickViewContent.querySelector('.quick-view-content__size-button--active, .quick-view-modal__size-button--active');
      const firstAvailable = allSizeBtns.find(b => b.getAttribute('data-available') !== 'false');
      const desiredActive = (previouslyActive && previouslyActive.getAttribute('data-available') !== 'false')
        ? previouslyActive
        : (firstAvailable || previouslyActive || allSizeBtns[0] || null);
      if (desiredActive && desiredActive !== previouslyActive) {
        allSizeBtns.forEach(b => b.classList.remove('quick-view-content__size-button--active', 'quick-view-modal__size-button--active'));
        desiredActive.classList.add('quick-view-content__size-button--active', 'quick-view-modal__size-button--active');
      }

      if (addToCartBtn && desiredActive) {
        // Drive ATC state from the chosen size button so it stays consistent with the buttons
        updateAddToCartButton(desiredActive, atcModal);
      }
    } else if (addToCartBtn) {
      // Single-option product (e.g. only Color, no Size). There are no size buttons to
      // color-filter, so resolve the variant directly from the seater's variants list:
      //   1) If user picked a color: REQUIRE a match in the new seater. If none exists,
      //      mark ATC Sold Out — silently swapping to the seater's default would add the
      //      wrong color (the bug the user reported: Charcoal -> Jet Black).
      //   2) If user has no color selection, use the seater's selected_or_first_available.
      let resolved = null;

      if (selectedColor && Array.isArray(seaterContext.variants) && seaterContext.colorOptionIndex >= 0) {
        const matchAvailable = seaterContext.variants.find(v =>
          v.options[seaterContext.colorOptionIndex] === selectedColor && v.available
        );
        const matchAny = seaterContext.variants.find(v =>
          v.options[seaterContext.colorOptionIndex] === selectedColor
        );
        const m = matchAvailable || matchAny;
        if (m) {
          resolved = {
            id: m.id,
            price: (productData.variantDisplayPrices || {})[String(m.id)] || formatMoney(m.price),
            available: m.available
          };
        } else {
          // Color exists in the user's mind but not in this seater. Disable ATC instead
          // of falling back to a different color.
          resolved = { id: null, price: null, available: false, missingColor: true };
        }
      } else {
        resolved = productData.selectedVariant || null;
      }

      if (resolved) {
        if (resolved.id != null) {
          addToCartBtn.setAttribute('data-id', resolved.id);
        } else {
          addToCartBtn.removeAttribute('data-id');
        }
        if (resolved.available) {
          addToCartBtn.disabled = false;
          addToCartBtn.removeAttribute('aria-disabled');
          addToCartBtn.textContent = 'Add to Cart - ' + resolved.price;
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.setAttribute('aria-disabled', 'true');
          addToCartBtn.textContent = 'Sold Out';
        }
      }
    }

    const fullDetailsLink = quickViewContent.querySelector('.quick-view-content__full-details');
    if (fullDetailsLink) {
      fullDetailsLink.href = productData.url;
    }
  }

  function rebuildSizeSelector(quickViewContent, productData) {
    let sizeSelector = quickViewContent.querySelector('.quick-view-content__size-selector');

    if (productData.sizeValues && productData.sizeValues.length > 0) {
      if (!sizeSelector) {
        const atcBtn = quickViewContent.querySelector('.quick-view-content__add-to-cart');
        if (!atcBtn) return;
        sizeSelector = document.createElement('div');
        sizeSelector.className = 'quick-view-content__size-selector';
        atcBtn.parentNode.insertBefore(sizeSelector, atcBtn);
      }

      sizeSelector.setAttribute('data-product-id', productData.id);
      sizeSelector.setAttribute('data-color-option-index', productData.colorOptionIndex);
      sizeSelector.innerHTML =
        '<h3 class="quick-view-content__size-title">PLEASE SELECT YOUR SIZE:</h3>' +
        '<div class="quick-view-content__size-buttons"></div>';

      const buttonsContainer = sizeSelector.querySelector('.quick-view-content__size-buttons');
      const displayPrices = productData.variantDisplayPrices || {};
      productData.sizeValues.forEach(function(sv, idx) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'quick-view-content__size-button';
        if (idx === 0) btn.classList.add('quick-view-content__size-button--active');
        btn.setAttribute('data-variant-id', sv.variantId);
        btn.setAttribute('data-size-value', sv.value);
        btn.setAttribute('data-size-option-index', productData.sizeOptionIndex);
        btn.setAttribute('data-price', displayPrices[String(sv.variantId)] || sv.price);
        btn.setAttribute('data-available', sv.available);
        btn.textContent = sv.value;
        buttonsContainer.appendChild(btn);
      });

      sizeSelector.removeAttribute('hidden');
      sizeSelector.style.display = '';

      const firstActiveBtn = buttonsContainer.querySelector('.quick-view-content__size-button--active');
      if (firstActiveBtn) {
        const modal = quickViewContent.closest('.quick-view-modal, .product-card__quick-view-overlay');
        if (modal) updateAddToCartButton(firstActiveBtn, modal);
      }
    } else if (sizeSelector) {
      sizeSelector.setAttribute('hidden', '');
    }
  }

  /** Prefer Liquid-computed display price (discount-ui-rs) when present on the quick view. */
  function getQuickViewVariantDisplayPrice(quickView, variantId) {
    if (variantId == null || variantId === '') return null;
    const script = quickView && quickView.querySelector && quickView.querySelector('.quick-view-variant-display-prices');
    if (!script) return null;
    try {
      const map = JSON.parse(script.textContent || '{}');
      return map[String(variantId)] || null;
    } catch (e) {
      return null;
    }
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    // Fallback
    return '$' + (cents / 100).toFixed(2);
  }

  function handleQuickViewClick(e) {
    const button = e.target.closest('[data-open-quick-view]');
    if (!button) return;
    
    e.preventDefault();
    e.stopPropagation();

    const productId = button.getAttribute('data-product-id');
    // Section id is what disambiguates two sections rendering the same product.
    // Read it from the button first, then fall back to the enclosing card.
    const sectionId = button.getAttribute('data-section-id')
      || (button.closest('.product-card') && button.closest('.product-card').getAttribute('data-section-id'))
      || null;
    openQuickView(productId, sectionId);
  }

  function handleCloseClick(e) {
    if (e.target.closest('[data-close-quick-view]') || e.target.closest('.quick-view-content__close')) {
      closeQuickView();
    }
  }

  function handleEscapeKey(e) {
    if (e.key === 'Escape' && currentModal?.classList.contains('is-open')) {
      closeQuickView();
    }
  }

  function handleVariantSelection(e) {
    const sizeButton = e.target.closest('.quick-view-modal__size-button, .quick-view-content__size-button');
    if (!sizeButton) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    if (sizeButton.classList.contains('quick-view-modal__size-button--active') || 
        sizeButton.classList.contains('quick-view-content__size-button--active')) {
      return;
    }
    
    selectVariant(sizeButton);
  }

  function handleAddToCart(e) {
    const button = e.target.closest('.quick-view-content__add-to-cart, .quick-view-modal__add-to-cart');
    if (!button || button.disabled || !button.classList.contains('js-add-to-cart')) return;
    
    // Show loader immediately
    showLoader(button);
    
    // Store button reference for cleanup
    const buttonId = button.getAttribute('data-id');
    if (buttonId) {
      button.dataset.loadingId = buttonId;
    }
    
    // Listen for cart completion (once: true auto-removes listener)
    const hideLoaderHandler = function() {
      const loadingButton = document.querySelector('.quick-view-content__add-to-cart.loading, .quick-view-modal__add-to-cart.loading');
      if (loadingButton) {
        hideLoader(loadingButton);
      }
    };
    
    window.addEventListener('cart:updated', hideLoaderHandler, { once: true });
    
    // Fallback: hide loader after 5 seconds if cart event doesn't fire
    setTimeout(() => {
      const loadingButton = document.querySelector('.quick-view-content__add-to-cart.loading, .quick-view-modal__add-to-cart.loading');
      if (loadingButton) {
        hideLoader(loadingButton);
      }
    }, 5000);
  }

  function showLoader(button) {
    button.classList.add('loading');
    button.disabled = true;
    const originalText = button.textContent.trim();
    button.dataset.originalText = originalText;
    button.innerHTML = '<span class="quick-view-loader"></span> Adding...';
  }

  function hideLoader(button) {
    if (!button) return;
    button.classList.remove('loading');
    button.disabled = false;
    const originalText = button.dataset.originalText || 'Add to Cart';
    button.textContent = originalText;
    delete button.dataset.originalText;
  }

  function closeAllQuickViews() {
    const closeModal = (modal) => {
      modal.classList.remove('is-open');
      // NOTE: We intentionally keep modal._seaterContext (and the mutated data-product-id)
      // across close/open. After a seater switch the DOM has been mutated to the new
      // product's title/size buttons/etc., so on re-open the in-memory variant context
      // must continue to match what the user sees. Clearing it would cause subsequent
      // color changes to resolve against the original product card's variants while
      // the size buttons in the DOM are for the switched seater.
      const productCard = modal.closest('.product-card');
      const productCardImage = modal.closest('.product-card__image');
      if (productCard) productCard.classList.remove('quick-view-open');
      if (productCardImage) productCardImage.classList.remove('quick-view-open');
    };
    
    document.querySelectorAll('.quick-view-modal.is-open, .product-card__quick-view-overlay.is-open').forEach(closeModal);
    
    document.body.style.overflow = '';
    currentModal = null;
  }

  function openQuickView(productId, sectionId) {
    closeAllQuickViews();
    
    const isMobile = window.innerWidth <= 768;
    // Section-scoped lookup so two sections rendering the same product each
    // open their OWN modal/overlay instead of always opening section 1's.
    const modal = isMobile
      ? findQuickViewModal(productId, sectionId)
      : findQuickViewOverlay(productId, sectionId);
    
    if (!modal) return;

    currentModal = modal;
    modal.classList.add('is-open');
    
    if (isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      const productCard = modal.closest('.product-card');
      const productCardImage = modal.closest('.product-card__image');
      if (productCard) productCard.classList.add('quick-view-open');
      if (productCardImage) productCardImage.classList.add('quick-view-open');
    }
    
    syncCurrentColorFromProductCard(productId, modal);
    
    const activeButton = modal.querySelector('.quick-view-content__size-button--active, .quick-view-modal__size-button--active');
    if (activeButton) {
      updateAddToCartButton(activeButton, modal);
    } else {
      const firstButton = modal.querySelector('.quick-view-content__size-button, .quick-view-modal__size-button');
      if (firstButton) selectVariant(firstButton);
    }
  }

  function closeQuickView() {
    closeAllQuickViews();
  }

  function selectVariant(button) {
    const modal = button.closest('.quick-view-modal, .product-card__quick-view-overlay');
    if (!modal || !modal.classList.contains('is-open')) return;

    const container = button.closest('.quick-view-modal__size-buttons, .quick-view-content__size-buttons');
    if (container) {
      container.querySelectorAll('.quick-view-modal__size-button, .quick-view-content__size-button').forEach(btn => {
        btn.classList.remove('quick-view-modal__size-button--active', 'quick-view-content__size-button--active');
      });
    }

    button.classList.add('quick-view-modal__size-button--active', 'quick-view-content__size-button--active');
    
    const productId = modal.getAttribute('data-product-id') || container?.getAttribute('data-product-id');
    if (productId) {
      updateVariantWithColor(button, modal, productId);
    } else {
      updateAddToCartButton(button, modal);
    }
  }
  
  function updateVariantWithColor(sizeButton, modal, productId) {
    // Get the user's currently-selected color (preferring the quick view's hidden input,
    // falling back to the source product card's checked swatch).
    const quickViewColorInput = modal.querySelector(`.quick-view-content__current-color[data-product-id="${productId}"]`)
      || modal.querySelector('.quick-view-content__current-color');
    let selectedColor = quickViewColorInput?.value || null;

    if (!selectedColor) {
      // Scope the product-card lookup to the modal's section so we read the
      // correct card when the same product is rendered across multiple sections.
      const sectionId = modal.getAttribute && modal.getAttribute('data-section-id');
      const productCard = findProductCard(productId, sectionId);
      const colorInput = productCard?.querySelector('.product-form__color-swatch-input:checked');
      selectedColor = colorInput?.value || null;
    }

    const sizeSelector = sizeButton.closest('.quick-view-content__size-selector');
    const sizeSelectorColorIdx = sizeSelector ? parseInt(sizeSelector.getAttribute('data-color-option-index')) : NaN;
    const sizeValue = sizeButton.getAttribute('data-size-value');
    const sizeOptionIndex = parseInt(sizeButton.getAttribute('data-size-option-index'));

    if (isNaN(sizeOptionIndex)) {
      updateAddToCartButton(sizeButton, modal);
      return;
    }

    // Resolve variant context: prefers the active seater data (if user switched seaters),
    // falls back to the source product card's variant-radios script.
    const ctx = getQuickViewVariantsContext(modal, productId, isNaN(sizeSelectorColorIdx) ? -1 : sizeSelectorColorIdx);
    if (!ctx || !Array.isArray(ctx.variants) || ctx.variants.length === 0) {
      updateAddToCartButton(sizeButton, modal);
      return;
    }
    const variants = ctx.variants;
    // Color option index priority: size selector's data attr (most specific to current view),
    // then context (which the seater switch sets correctly).
    const colorOptionIndex = !isNaN(sizeSelectorColorIdx) ? sizeSelectorColorIdx : ctx.colorOptionIndex;

    let matchingVariant = null;
    if (selectedColor && colorOptionIndex >= 0 && sizeValue) {
      matchingVariant = variants.find(v =>
        v.options[sizeOptionIndex] === sizeValue && v.options[colorOptionIndex] === selectedColor
      );
    }
    if (!matchingVariant && sizeValue) {
      matchingVariant = variants.find(v => v.options[sizeOptionIndex] === sizeValue);
    }
    if (!matchingVariant) {
      const variantId = sizeButton.getAttribute('data-variant-id');
      matchingVariant = variants.find(v => String(v.id) === String(variantId));
    }

    if (matchingVariant) {
      sizeButton.setAttribute('data-variant-id', matchingVariant.id);
      const displayPrice = (ctx.variantDisplayPrices && ctx.variantDisplayPrices[String(matchingVariant.id)])
        || getQuickViewVariantDisplayPrice(modal, matchingVariant.id);
      sizeButton.setAttribute('data-price', displayPrice || formatMoney(matchingVariant.price));
      // If a color is selected, availability must be evaluated against that exact color
      // (a variant matched on size-only with a different color is NOT what the user is buying).
      const colorMatches = !selectedColor || colorOptionIndex < 0
        ? true
        : matchingVariant.options[colorOptionIndex] === selectedColor;
      const isAvailable = Boolean(matchingVariant.available && colorMatches);
      sizeButton.setAttribute('data-available', isAvailable ? 'true' : 'false');
    } else if (selectedColor && sizeValue) {
      sizeButton.removeAttribute('data-variant-id');
      sizeButton.setAttribute('data-available', 'false');
    }

    updateAddToCartButton(sizeButton, modal);
  }

  function updateAddToCartButton(button, modal) {
    // Use provided modal or find it from the button
    if (!modal) {
      modal = button.closest('.quick-view-modal, .product-card__quick-view-overlay');
    }
    if (!modal) return;
    
    // Check if modal is open (for overlays, they might not have is-open class immediately)
    const isOpen = modal.classList.contains('is-open') || 
                   modal.classList.contains('quick-view-open') ||
                   modal.style.display !== 'none';
    
    if (!isOpen && !modal.classList.contains('is-open')) {
      // Still try to update if it's a quick view element
      const isQuickView = modal.classList.contains('quick-view-modal') || 
                         modal.classList.contains('product-card__quick-view-overlay');
      if (!isQuickView) return;
    }

    const addToCartButton = modal.querySelector('.quick-view-modal__add-to-cart, .quick-view-content__add-to-cart');
    if (!addToCartButton) {
      console.warn('Add to cart button not found in quick view', modal);
      return;
    }

    const variantId = button.getAttribute('data-variant-id');
    const price = getQuickViewVariantDisplayPrice(modal, variantId) || button.getAttribute('data-price');
    
    // Check variant availability
    const availableAttr = button.getAttribute('data-available');
    const isAvailable = availableAttr !== 'false' && availableAttr !== null;
    
    if (variantId && isAvailable) {
      addToCartButton.setAttribute('data-id', variantId);
    } else {
      // Remove variant ID if unavailable or not found
      addToCartButton.removeAttribute('data-id');
    }
    
    addToCartButton.disabled = !isAvailable;
    if (!isAvailable) {
      addToCartButton.setAttribute('aria-disabled', 'true');
      addToCartButton.textContent = 'Sold Out';
    } else {
      addToCartButton.removeAttribute('aria-disabled');
      if (price) {
        addToCartButton.textContent = `Add to Cart - ${price}`;
      } else {
        // Keep existing text if no price
        const existingText = addToCartButton.textContent || 'Add to Cart';
        if (!existingText.includes('Sold Out')) {
          addToCartButton.textContent = existingText;
        }
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

